import { el, replaceChildren, type Child } from '../../lib/dom'
import type { DocState, TintRange } from './replayState'

export type ConsoleTypeout = {
  readonly element: HTMLElement
  readonly render: (state: DocState) => void
  readonly flash: () => void
  readonly think: (label: string) => void
}

const FLASH_MS = 140
const THINK_MS = 1200
/** Keep this much room under the caret so the next line is already visible. */
const CARET_MARGIN_PX = 24

const segments = (doc: string, tints: readonly TintRange[]): Child[] => {
  const sorted = [...tints].sort((a, b) => a.from - b.from)
  const out: Child[] = []
  let cursor = 0
  for (const tint of sorted) {
    const from = Math.max(cursor, tint.from)
    const to = Math.min(doc.length, tint.to)
    if (to <= from) continue
    if (from > cursor) out.push(doc.slice(cursor, from))
    out.push(el('span', { class: `tint tint-${tint.kind}` }, doc.slice(from, to)))
    cursor = to
  }
  if (cursor < doc.length) out.push(doc.slice(cursor))
  return out
}

/** The code typing itself out, with a caret, tints and a thinking label. */
export const createConsoleTypeout = (): ConsoleTypeout => {
  const code = el('code', { class: 'typeout-code' })
  const caret = el('span', { class: 'typeout-caret', 'aria-hidden': 'true' })
  const pre = el('pre', { class: 'typeout', 'aria-live': 'off', tabindex: '0' }, code, caret)
  const thought = el('span', { class: 'typeout-thought', role: 'status' })
  const element = el('div', { class: 'typeout-wrap' }, pre, thought)
  let flashTimer: number | null = null
  let thinkTimer: number | null = null

  const keepCaretVisible = (): void => {
    const bottom = caret.offsetTop + caret.offsetHeight
    if (bottom > pre.scrollTop + pre.clientHeight || caret.offsetTop < pre.scrollTop) {
      pre.scrollTop = Math.max(0, bottom - pre.clientHeight + CARET_MARGIN_PX)
    }
  }

  return {
    element,
    render: (state) => {
      replaceChildren(code, segments(state.doc, state.tints))
      keepCaretVisible()
    },
    flash: () => {
      pre.classList.add('is-flash')
      if (flashTimer !== null) clearTimeout(flashTimer)
      flashTimer = window.setTimeout(() => pre.classList.remove('is-flash'), FLASH_MS)
    },
    think: (label) => {
      thought.textContent = label
      thought.classList.add('is-visible')
      if (thinkTimer !== null) clearTimeout(thinkTimer)
      thinkTimer = window.setTimeout(() => thought.classList.remove('is-visible'), THINK_MS)
    },
  }
}
